import type {
  ColDef,
  FirstDataRenderedEvent,
  ITooltipParams,
  SelectionChangedEvent,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";
import type { AgGridReactProps } from "ag-grid-react";
import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";

import {
  PreviewItemRef,
  useOrderMarkets,
  useOrders,
  useSessionUser,
  useTimezone,
} from "@web-app/api";
import { Input, LoadingIcon, OrdersFilterDropdown } from "@web-app/components";
import { useBrowserCache } from "@web-app/hooks";
import MarketsDropdown from "@web-app/pages/common/MarketsDropdown";
import { INITIAL_APP_FILTER_DATA } from "@web-app/pages/common/Orders/data";
import { Order, OrderDeliveryType } from "@web-app/types";

import {
  formatOptimizationDateTime,
  isAlreadyDispatched,
  metersToMiles,
} from "../utils";

const AgGridTable = lazy<React.ComponentType<AgGridReactProps>>(
  () => import("@web-app/components/AgGridTable"),
);

type SelectItemsStepProps = {
  onChange: (items: PreviewItemRef[]) => void;
  selectedItems: PreviewItemRef[];
};

type RowKind = "ORDER" | "ROUTE" | "STOP";

type RowData = {
  arrival: string;
  distance: number | null;
  dropoff: string;
  externalId: string;
  id: string;
  itemId?: string;
  kind: RowKind;
  orderId: string;
  path: string[];
  pickup: string;
  price: number;
};

const rowKey = (row: { itemId?: string; kind: string }) =>
  `${row.kind}:${row.itemId}`;

const routePrice = (orders: Order[]) =>
  orders.reduce((sum, order) => sum + (order.details?.value || 0), 0);

const billableStops = (orders: Order[]) => {
  const pickups = orders.filter(
    (order) => order.type === OrderDeliveryType.ROUTE_OPT_PICKUP,
  );
  const dropoffs = orders.filter(
    (order) => order.type === OrderDeliveryType.ROUTE_OPT_DROPOFF,
  );
  return pickups.length > dropoffs.length ? pickups : dropoffs;
};

const priceFormatter = (p: ValueFormatterParams) =>
  `$${Number(p.value ?? 0).toFixed(2)}`;

const distanceFormatter = (p: ValueFormatterParams) =>
  metersToMiles((p.value as number) ?? null);

const addressTooltip = (p: ITooltipParams) => (p.value as string) || "";

const columnDefs: ColDef<RowData>[] = [
  {
    field: "price",
    headerName: "Price",
    valueFormatter: priceFormatter,
    width: 110,
  },
  {
    field: "pickup",
    flex: 1,
    headerName: "Pickup Address",
    minWidth: 180,
    tooltipValueGetter: addressTooltip,
  },
  {
    field: "dropoff",
    flex: 1,
    headerName: "Drop-off Address",
    minWidth: 180,
    tooltipValueGetter: addressTooltip,
  },
  { field: "arrival", headerName: "Dropoff Arrival", width: 150 },
  { field: "orderId", headerName: "Order/Route ID", width: 150 },
  {
    field: "distance",
    headerName: "Distance",
    valueFormatter: distanceFormatter,
    width: 120,
  },
];

const autoGroupColumnDef = {
  cellRendererParams: {
    checkbox: (params: { node: { level: number } }) => params.node.level === 0,
    suppressCount: true,
  },
  flex: 1,
  headerCheckboxSelection: true,
  headerName: "External ID",
  minWidth: 240,
  tooltipValueGetter: addressTooltip,
  valueGetter: (params: ValueGetterParams<RowData>) => params.data?.externalId,
};

const defaultColDef: ColDef = {
  resizable: true,
  sortable: true,
  suppressHeaderMenuButton: true,
};

const getRowId = (params: { data: RowData }) => params.data.id;

const getDataPath = (data: RowData) => data.path;

export default function SelectItemsStep({
  onChange,
  selectedItems,
}: SelectItemsStepProps) {
  const [appFilter, setAppFilter] = useBrowserCache(
    "cpdAppFilter",
    INITIAL_APP_FILTER_DATA,
  );
  const { data: ordersData, isLoading } = useOrders(appFilter);
  const { data: sessionUser } = useSessionUser();
  const { getTimezone } = useTimezone();
  const { data: marketsMap } = useOrderMarkets({
    countryCode: appFilter.countryCode,
  });
  const [search, setSearch] = useState("");
  const seededMarketsRef = useRef(false);

  const currentWorkspaceId = sessionUser?.currentWorkspaceId || "";

  // The orders endpoint returns nothing when stateIds is empty, so default to
  // all markets (plus "null" for orders without a state) on first load.
  useEffect(() => {
    if (seededMarketsRef.current) return;
    if (appFilter.stateIds && appFilter.stateIds.length > 0) {
      seededMarketsRef.current = true;
      return;
    }
    if (!marketsMap || marketsMap.size === 0) return;
    const allStateIds = Array.from(marketsMap.values()).map(
      (state) => state.id,
    );
    seededMarketsRef.current = true;
    setAppFilter({ ...appFilter, stateIds: [...allStateIds, "null"] });
  }, [marketsMap, appFilter, setAppFilter]);

  const rows = useMemo<RowData[]>(() => {
    const routeOrders = new Map<string, Order[]>();
    const routeInfo = new Map<
      string,
      { distance: number | null; externalId: string }
    >();
    const p2pOrders: Order[] = [];

    Array.from(ordersData?.orders.values() || []).forEach((order) => {
      if (order.workspaceId !== currentWorkspaceId) {
        return;
      }
      if (order.routeId && order.route) {
        if (isAlreadyDispatched(order.route, currentWorkspaceId)) {
          return;
        }
        const list = routeOrders.get(order.route.id) || [];
        list.push(order);
        routeOrders.set(order.route.id, list);
        if (!routeInfo.has(order.route.id)) {
          routeInfo.set(order.route.id, {
            distance: order.route.estimatedDistance ?? null,
            externalId: order.route.externalId || order.route.displayId,
          });
        }
      } else if (
        order.type === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF &&
        !isAlreadyDispatched(order, currentWorkspaceId)
      ) {
        p2pOrders.push(order);
      }
    });

    const result: RowData[] = [];

    routeOrders.forEach((orders, routeId) => {
      // A route sent to a DSP is delegated stop by stop, so the route document
      // is not always the one carrying the hand-off.
      if (
        orders.every((order) => isAlreadyDispatched(order, currentWorkspaceId))
      ) {
        return;
      }
      const info = routeInfo.get(routeId);
      const first = orders[0];
      const last = orders[orders.length - 1];
      result.push({
        arrival: formatOptimizationDateTime(
          first?.scheduledStartDateTime,
          getTimezone(first?.timezone),
        ),
        distance: info?.distance ?? null,
        dropoff: last?.dropoff?.address || "—",
        externalId: info?.externalId || routeId,
        id: routeId,
        itemId: routeId,
        kind: "ROUTE",
        orderId: routeId,
        path: [routeId],
        pickup: first?.pickup?.address || "—",
        price: routePrice(orders),
      });
      billableStops(orders).forEach((stop) => {
        result.push({
          arrival: formatOptimizationDateTime(
            stop.scheduledEndDateTime,
            getTimezone(stop.timezone),
          ),
          distance: stop.estimatedDistance ?? null,
          dropoff: stop.dropoff?.address || "—",
          externalId: stop.externalId || "—",
          id: stop.id,
          kind: "STOP",
          orderId: stop.displayId,
          path: [routeId, stop.id],
          pickup: stop.pickup?.address || "—",
          price: stop.details?.value || 0,
        });
      });
    });

    p2pOrders.forEach((order) => {
      result.push({
        arrival: formatOptimizationDateTime(
          order.scheduledEndDateTime,
          getTimezone(order.timezone),
        ),
        distance: order.estimatedDistance ?? null,
        dropoff: order.dropoff?.address || "—",
        externalId: order.externalId || "—",
        id: order.id,
        itemId: order.id,
        kind: "ORDER",
        orderId: order.displayId,
        path: [order.id],
        pickup: order.pickup?.address || "—",
        price: order.details?.value || 0,
      });
    });

    return result;
  }, [ordersData, currentWorkspaceId]);

  const availableCount = rows.filter((row) => row.kind !== "STOP").length;

  const handleSelectionChanged = (event: SelectionChangedEvent<RowData>) => {
    onChange(
      event.api
        .getSelectedRows()
        .filter((row) => row.kind !== "STOP" && row.itemId)
        .map((row) => ({
          itemId: row.itemId as string,
          kind: row.kind as "ORDER" | "ROUTE",
        })),
    );
  };

  const handleFirstDataRendered = (event: FirstDataRenderedEvent<RowData>) => {
    const keys = new Set(selectedItems.map((item) => rowKey(item)));
    event.api.forEachNode((node) => {
      if (node.data && node.level === 0) {
        node.setSelected(keys.has(rowKey(node.data)));
      }
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Input puts className on the field, so the flex item around it still
            shrinks and clips the placeholder without a width of its own. */}
        <div className="w-96 shrink-0">
          <Input
            type="search"
            value={search}
            onChange={setSearch}
            placeholder="Search Order ID or External ID"
            className="w-full !rounded-[23px]"
          />
        </div>
        <div className="flex items-center gap-3">
          <MarketsDropdown appFilter={appFilter} setAppFilter={setAppFilter} />
          <OrdersFilterDropdown
            appFilter={appFilter}
            setAppFilter={setAppFilter}
            isDateRange
          />
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-700">Available ({availableCount})</span>
        <span className="text-gray-700">Selected ({selectedItems.length})</span>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-12">
          <LoadingIcon className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="ag-theme-quartz h-[520px] w-full">
          <Suspense
            fallback={
              <div className="flex items-center justify-center p-12">
                <LoadingIcon className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            }
          >
            <AgGridTable
              rowData={rows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              autoGroupColumnDef={autoGroupColumnDef}
              treeData
              getDataPath={getDataPath}
              getRowId={getRowId}
              rowSelection="multiple"
              suppressRowClickSelection
              quickFilterText={search}
              rowHeight={48}
              animateRows
              onSelectionChanged={handleSelectionChanged}
              onFirstDataRendered={handleFirstDataRendered}
              overlayNoRowsTemplate="No routes or point-to-point orders match your filters."
            />
          </Suspense>
        </div>
      )}
    </div>
  );
}
